import ActionButton from '@repo/ui/action-button'
import { Column } from '@repo/ui/data-table'
import { PenIcon, PlusIcon, SettingIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import React from 'react'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { createFormula } from '../../../api/formula/create-formula'
import { updateFormula } from '../../../api/formula/update-formula'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import UserNavBar from '../../../components/elements/nav-bar'
import FormulaWishListForm from '../../../components/pages/formula-wish-list/formula-wish-list-form'
import { mapFormulaToInput } from '../../../components/pages/journey-places/formula/utils'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { Formula } from '../../../lib/types/models/formula/formula'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { t } from '../../../locale/utils'

const FormulasWishListPage = () => {
  const tableRef = React.useRef<DynamicTableRef<Formula>>(null)

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'edit' | 'create',
    Formula
  >(['edit', 'create'])

  const { mutateAsync: mutateEdit } = useMutation({
    mutationFn: updateFormula,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('edit')
      toast.success('Formule modifiée avec succès')
    }
  })

  const { mutateAsync } = useMutation({
    mutationFn: createFormula,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('create')
      toast.success('Formule créée avec succès')
    }
  })

  const columns: Column<Formula>[] = [
    {
      accessorKey: 'id',
      header: 'Id',
      enableSorting: true
    },
    {
      accessorKey: 'code',
      header: 'Code',
      enableSorting: true
    },
    {
      accessorKey: 'title',
      header: 'NOM',
      enableSorting: true,
      cell: ({ row }) => getTranslatedName(row.original)
    },
    {
      accessorKey: 'description',
      header: 'Description',
      enableSorting: true,
      cell: ({ row }) => <span>{row.original.description ?? '-'}</span>
    },
    {
      accessorKey: 'category',
      header: 'Catégorie',
      enableSorting: true,
      cell: ({ row }) => (!row.original.category ? '-' : row.original.category)
    },
    {
      accessorKey: 'id',
      header: '',
      cell: ({ row }) => (
        <ActionButton
          variant={'grey'}
          icon={PenIcon}
          onClick={() => toggleModal('edit', row.original)}
        />
      )
    }
  ]

  return (
    <div>
      <UserNavBar title="Formule" icon={SettingIcon} />
      <div className="h-4" />
      <DynamicDataTable<Formula>
        ref={tableRef}
        title="Formule"
        columns={columns}
        deleteMessage={{
          name: 'formule',
          attributeName: 'id',
          deleteOne: (data) =>
            t('dynamic.data.table.delete.one.label', {
              message: `formule "${getTranslatedName(data)}"`
            }),
          deleteMany: (data) =>
            t('dynamic.data.table.delete.many.label', {
              message: `formule "${data.map((row) => getTranslatedName(row)).join('", "')}"`
            })
        }}
        selectable
        allowSearchParam
        endpoint={API.formulas.getAll.url}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Créer',
            fn: () => toggleModal('create')
          }
        ]}
      />

      {/* Edit */}
      {selectedData && (
        <Modal
          icon={SettingIcon}
          isOpen={modalState.edit}
          title="Modifier"
          hideActions
          onCancel={() => toggleModal('edit')}
        >
          <FormulaWishListForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={mapFormulaToInput(selectedData)}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('edit'),
              onConfirm: async (values) => {
                if (selectedData) await mutateEdit({ id: selectedData.id, ...values })
              }
            })}
          />
        </Modal>
      )}

      {/* Create */}
      <Modal
        icon={SettingIcon}
        isOpen={modalState.create}
        title="Créer"
        hideActions
        onCancel={() => toggleModal('create')}
      >
        <FormulaWishListForm
          defaultValues={{
            displayLongVersionForClientSpace: false,
            category: '',
            code: '',
            description: '',
            translations: []
          }}
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('create'),
            onConfirm: mutateAsync
          })}
        />
      </Modal>
    </div>
  )
}

export default FormulasWishListPage
